import axios from 'axios';
import { isInLoginGracePeriod, invalidateAuthOnUnauthorized } from './api';

const appointmentAPI = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
appointmentAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros
appointmentAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    invalidateAuthOnUnauthorized(error, 'AppointmentAPI');
    return Promise.reject(error);
  }
);

export interface Appointment {
  id: string;
  property_id: string;
  employee_id: string;
  scheduled_date: string;
  duration: number; // Tempo estimado em minutos
  actual_duration?: number; // Tempo real em minutos
  start_time?: string;
  end_time?: string;
  price: number;
  status: string;
  special_instructions?: string;
}

export interface UpdateAppointmentRequest {
  property_id?: string;
  employee_id?: string;
  scheduled_date?: string;
  duration?: number;
  actual_duration?: number;
  start_time?: string;
  end_time?: string;
  price?: number;
  status?: string;
  special_instructions?: string;
}

export interface CompleteServiceRequest {
  actual_duration?: number; // Se não informado, usa duration
  end_time?: string; // Se não informado, calcula automaticamente
  notes?: string;
}

// Buscar agendamentos
export const getAppointments = async (params?: {
  start_date?: string;
  end_date?: string;
  status?: string;
  employee_id?: string;
  client_id?: string;
}) => {
  // Verificar se token está disponível antes de fazer requisição
  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('[AppointmentAPI] Tentativa de buscar agendamentos sem token');
    return null;
  }

  // Verificar se estamos no período de graça após login
  // Se estiver, adicionar um pequeno delay para garantir que o token está propagado
  if (isInLoginGracePeriod()) {
    // Aguardar um pouco mais para garantir que o token está totalmente disponível
    // e que o backend reconheceu o token
    await new Promise(resolve => setTimeout(resolve, 200));
  }

  try {
    const response = await appointmentAPI.get('/appointments', { params });
    return response.data || null;
  } catch (error: any) {
    // Log detalhado do erro para debug (apenas em desenvolvimento)
    if (process.env.NODE_ENV === 'development') {
      if (error.response) {
        // Erro de resposta do servidor
        console.warn('Erro ao buscar agendamentos:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.config?.url,
        });
      } else if (error.request) {
        // Erro de rede - servidor não respondeu
        console.warn('Erro de rede ao buscar agendamentos:', {
          message: error.message,
          code: error.code,
        });
      } else {
        // Outro tipo de erro
        console.warn('Erro ao buscar agendamentos:', error.message);
      }
    }
    
    // Se for erro 401, o interceptor já deve ter redirecionado
    if (error.response?.status === 401) {
      return null;
    }
    
    // Se for erro 404 ou 500, retornar null silenciosamente
    if (error.response?.status === 404 || error.response?.status === 500) {
      return null;
    }
    
    // Para erros de rede (servidor não respondeu), retornar null
    // Isso permite que a aplicação continue funcionando mesmo sem conexão
    if (error.request && !error.response) {
      return null;
    }
    
    // Retornar null em caso de erro para evitar crashes
    return null;
  }
};

// Criar agendamento
export const createAppointment = async (data: {
  property_id: string;
  employee_id: string;
  scheduled_date: string;
  duration: number;
  price?: number;
  special_instructions?: string;
}) => {
  const response = await appointmentAPI.post('/appointments', data);
  return response.data;
};

// Atualizar agendamento
export const updateAppointment = async (
  id: string,
  data: UpdateAppointmentRequest
) => {
  const response = await appointmentAPI.put(`/appointments/${id}`, data);
  return response.data;
};

// Finalizar serviço (atualizar tempo real)
export const completeService = async (
  id: string,
  data: CompleteServiceRequest
) => {
  const response = await appointmentAPI.patch(`/appointments/${id}/complete`, data);
  return response.data;
};

// Obter agendamento por ID
export const getAppointmentById = async (id: string) => {
  const response = await appointmentAPI.get(`/appointments/${id}`);
  return response.data;
};

// Deletar/Cancelar agendamento
export const deleteAppointment = async (id: string) => {
  const response = await appointmentAPI.delete(`/appointments/${id}`);
  return response.data;
};

// Buscar análise de tempo
export const getTimeAnalysis = async (appointmentId: string) => {
  const response = await appointmentAPI.get(`/time-tracking/appointment/${appointmentId}`);
  return response.data;
};

export default appointmentAPI;

